// Global confirmation dialog. Imperative + promise-based so any component can
// `await confirmDialog({ ... })` without prop-drilling or wiring local state.
// Mirrors the Toaster singleton pattern (module-level custom-event emitter plus
// a single host component mounted once at the app root).
//
// Usage:
//   import { confirmDialog } from "@/components/ConfirmDialog";
//   if (!(await confirmDialog({ title: "Delete file?", danger: true }))) return;
//   doDelete();
//
// <ConfirmHost /> is mounted once at the App root (App.tsx) so it is available
// on both the auth-gated workspace routes and the public routes.

import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Modal } from "@/proton/ui/Modal";
import { Button } from "@/proton/ui/Button";

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  // When true, the confirm button uses the danger style and defaults its label
  // to "Delete".
  danger?: boolean;
  // When present the dialog collects a line of text. promptDialog() wraps this
  // and resolves the string (or null if cancelled) instead of a boolean.
  input?: {
    label?: string;
    placeholder?: string;
    initialValue?: string;
    // Blocks confirm while the trimmed value is empty. Default true.
    required?: boolean;
  };
}

interface ConfirmRequest extends ConfirmOptions {
  id: string;
}

const EVT = "__l7_confirm";

// Pending resolvers keyed by request id. The host resolves and deletes the
// entry once the user answers (or dismisses). The payload is the typed value
// for input dialogs, and true/false otherwise; null always means dismissed.
type Answer = boolean | string | null;
const resolvers = new Map<string, (value: Answer) => void>();

/**
 * Show the confirmation dialog. Resolves true on confirm, false on cancel,
 * backdrop click, or Escape.
 */
export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  const id = crypto.randomUUID();
  const detail: ConfirmRequest = { ...opts, id };
  return new Promise<boolean>((resolve) => {
    resolvers.set(id, (v) => resolve(v === true));
    window.dispatchEvent(new CustomEvent<ConfirmRequest>(EVT, { detail }));
  });
}

/**
 * Ask for a line of text. Resolves the trimmed string, or null if dismissed.
 *
 * Replaces window.prompt(), which rendered the browser's own OS dialog in the
 * middle of the Aurora UI and cannot be styled, focus-managed, or themed.
 */
export function promptDialog(
  opts: Omit<ConfirmOptions, "input"> & { input?: ConfirmOptions["input"] },
): Promise<string | null> {
  const id = crypto.randomUUID();
  const detail: ConfirmRequest = { ...opts, id, input: opts.input ?? {} };
  return new Promise<string | null>((resolve) => {
    resolvers.set(id, (v) => resolve(typeof v === "string" ? v : null));
    window.dispatchEvent(new CustomEvent<ConfirmRequest>(EVT, { detail }));
  });
}

function settle(id: string, value: Answer): void {
  const resolve = resolvers.get(id);
  if (resolve) {
    resolvers.delete(id);
    resolve(value);
  }
}

// Module-level open flag.
//
// Surfaces with their own window-level keyboard handlers (the mail triage
// shortcuts, most notably) need to stand down while a dialog owns the screen.
// Without this, "Permanently delete?" could be on screen while `e` archived the
// message behind it and Escape both cancelled the dialog and closed the reader.
let dialogOpen = false;
export function isConfirmOpen(): boolean {
  return dialogOpen;
}

export function ConfirmHost(): JSX.Element {
  const [req, setReq] = useState<ConfirmRequest | null>(null);
  const [value, setValue] = useState("");
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    function onConfirm(e: Event) {
      const detail = (e as CustomEvent<ConfirmRequest>).detail;
      // Only one dialog at a time: if a prior one is still open, treat it as
      // cancelled before showing the new request.
      setValue(detail.input?.initialValue ?? "");
      setReq((curr) => {
        if (curr) settle(curr.id, null);
        return detail;
      });
    }
    window.addEventListener(EVT, onConfirm as EventListener);
    return () => window.removeEventListener(EVT, onConfirm as EventListener);
  }, []);

  // Focus on open, restore on close. Destructive dialogs focus Cancel (the safe
  // choice) so a reflexive Space/Enter does not fire the delete; benign dialogs
  // focus Confirm so the happy path stays one keystroke. Returning focus to the
  // element that opened the dialog keeps keyboard and screen-reader users where
  // they were instead of dumping them at <body>.
  useEffect(() => {
    dialogOpen = !!req;
    if (!req) return;
    const opener = document.activeElement as HTMLElement | null;
    if (req.input) {
      inputRef.current?.focus();
      inputRef.current?.select();
    } else if (req.danger === true) {
      cancelBtnRef.current?.focus();
    } else {
      confirmBtnRef.current?.focus();
    }
    return () => {
      dialogOpen = false;
      if (opener && document.contains(opener)) opener.focus();
    };
  }, [req]);

  // Keyboard: Escape always cancels. Enter confirms ONLY for benign dialogs AND
  // only when the user is not sitting on Cancel — activating the focused
  // control is what Enter means, and firing Confirm from a focused Cancel did
  // the exact opposite of what the user asked for. For danger dialogs Enter is
  // swallowed outright so a buffered keypress can never trigger an
  // irreversible action without the user aiming at the button.
  //
  // Capture phase, because other surfaces bind their own window-level handlers;
  // this dialog owns the keyboard while it is open.
  useEffect(() => {
    if (!req) return;
    const danger = req.danger === true;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        close(false);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        if (danger && !req!.input) return;                   // absorb
        if (document.activeElement === cancelBtnRef.current) { // honour the focused control
          close(false);
          return;
        }
        submit();
        return;
      }
      if (e.key === "Tab") {
        // Focus trap: only this dialog's own controls are reachable while open.
        const focusables = [inputRef.current, cancelBtnRef.current, confirmBtnRef.current]
          .filter((b): b is HTMLInputElement | HTMLButtonElement => !!b);
        if (focusables.length === 0) return;
        e.preventDefault();
        const idx = focusables.indexOf(document.activeElement as HTMLInputElement | HTMLButtonElement);
        const next = e.shiftKey
          ? focusables[(idx <= 0 ? focusables.length : idx) - 1]
          : focusables[(idx + 1) % focusables.length];
        next.focus();
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req]);

  function close(ok: boolean) {
    setReq((curr) => {
      if (curr) settle(curr.id, ok);
      return null;
    });
  }

  // Confirm. For an input dialog the answer is the typed string, and a required
  // field that is still empty simply refuses rather than resolving nothing.
  function submit() {
    setReq((curr) => {
      if (!curr) return null;
      if (curr.input) {
        const v = value.trim();
        if ((curr.input.required ?? true) && !v) {
          inputRef.current?.focus();
          return curr;
        }
        settle(curr.id, v);
        return null;
      }
      settle(curr.id, true);
      return null;
    });
  }

  if (!req) return <></>;

  const danger = req.danger === true;
  const confirmLabel = req.confirmLabel ?? (danger ? "Delete" : "Confirm");
  const cancelLabel = req.cancelLabel ?? "Cancel";

  // Proton Prompt (components/prompt): 288px dialog, bold title, message,
  // then the actions stacked full width, the main one first.
  return (
    <Modal
      size="prompt"
      role="alertdialog"
      labelledBy="confirm-title"
      describedBy={req.message ? "confirm-message" : undefined}
      closeOnEscape={false}
      onClose={() => close(false)}
    >
      <div className="prompt-header">
        <h1 id="confirm-title" className="text-lg text-bold m-0">{req.title}</h1>
      </div>
      <div className="modal-two-content">
        {req.message && <p id="confirm-message" className="m-0">{req.message}</p>}
        {req.input && (
          <div className={clsx(req.message && "mt-3")}>
            {req.input.label && (
              <label htmlFor="confirm-input" className="block mb-1 text-semibold">
                {req.input.label}
              </label>
            )}
            <div className="input">
              <input
                id="confirm-input"
                ref={inputRef}
                type="text"
                className="input-element"
                value={value}
                placeholder={req.input.placeholder}
                onChange={(e) => setValue(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>
      <div className="modal-two-footer prompt-footer">
        <div className="flex flex-col gap-2">
          <Button
            ref={confirmBtnRef}
            color={danger ? "danger" : "norm"}
            fullWidth
            onClick={submit}
            disabled={!!req.input && (req.input.required ?? true) && !value.trim()}
          >
            {confirmLabel}
          </Button>
          <Button ref={cancelBtnRef} fullWidth onClick={() => close(false)}>
            {cancelLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
